
import React, { useState, useEffect } from 'react'
import { lumi } from '../lib/lumi'
import {Plus, Search, Filter, Edit, Trash2, Copy, Tag, Globe, HelpCircle} from 'lucide-react'
import toast from 'react-hot-toast'
import RichTextEditor from '../components/RichTextEditor'

interface Question {
  _id: string
  type: 'multiple_choice' | 'single_choice' | 'multiple_select' | 'ordered_multiple' | 'dropdown' | 'matrix_dropdown' | 'dropdown_matrix' | 'media' | 'star_rating' | 'rating_matrix' | 'slider' | 'slider_matrix' | 'ranking' | 'classification' | 'best_worst' | 'open_text' | 'open_number' | 'multiple_textboxes' | 'datetime' | 'email'
  translations: {
    pt: {
      title: string
      description?: string
      options?: string[]
    }
    en: {
      title: string
      description?: string
      options?: string[]
    }
    es: {
      title: string
      description?: string
      options?: string[]
    }
  }
  isRequired: boolean
  category?: string
  tags?: string[]
  settings?: any
  mediaUrl?: string
  createdAt: string
  updatedAt?: string
}

const asRecord = (value: unknown): Record<string, unknown> => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
)

const asString = (value: unknown, fallback = '') => (
  typeof value === 'string' ? value : fallback
)

const normalizeOptions = (value: unknown): string[] => {
  if (!Array.isArray(value)) return []

  return value
    .map((item) => {
      if (typeof item === 'string') return item
      const option = asRecord(item)
      return asString(option.text, asString(option.label, asString(option.value)))
    })
    .filter((item) => item.trim().length > 0)
}

const normalizeQuestionRecord = (value: unknown, index = 0): Question => {
  const source = asRecord(value)
  const translations = asRecord(source.translations)
  const pt = asRecord(translations.pt)
  const en = asRecord(translations.en)
  const es = asRecord(translations.es)
  const legacyTitle = asString(source.title)
  const legacyDescription = asString(source.description)
  const legacyOptions = normalizeOptions(source.options)

  const ptTitle = asString(pt.title, legacyTitle) || 'Pergunta sem título'
  const ptDescription = asString(pt.description, legacyDescription)
  const ptOptions = normalizeOptions(pt.options)
  const resolvedPtOptions = ptOptions.length > 0 ? ptOptions : legacyOptions

  const enTitle = asString(en.title, ptTitle)
  const enDescription = asString(en.description, ptDescription)
  const enOptions = normalizeOptions(en.options)

  const esTitle = asString(es.title, ptTitle)
  const esDescription = asString(es.description, ptDescription)
  const esOptions = normalizeOptions(es.options)

  return {
    _id: asString(source._id, asString(source.id, `legacy-question-${index}`)),
    type: (asString(source.type, 'open_text') || 'open_text') as Question['type'],
    translations: {
      pt: {
        title: ptTitle,
        description: ptDescription || undefined,
        options: resolvedPtOptions.length > 0 ? resolvedPtOptions : undefined,
      },
      en: {
        title: enTitle,
        description: enDescription || undefined,
        options: (enOptions.length > 0 ? enOptions : resolvedPtOptions).length > 0
          ? (enOptions.length > 0 ? enOptions : resolvedPtOptions)
          : undefined,
      },
      es: {
        title: esTitle,
        description: esDescription || undefined,
        options: (esOptions.length > 0 ? esOptions : resolvedPtOptions).length > 0
          ? (esOptions.length > 0 ? esOptions : resolvedPtOptions)
          : undefined,
      },
    },
    isRequired: source.isRequired === true || source.required === true,
    category: asString(source.category) || undefined,
    tags: Array.isArray(source.tags)
      ? source.tags.filter((tag): tag is string => typeof tag === 'string' && tag.trim().length > 0)
      : [],
    settings: source.settings,
    mediaUrl: asString(source.mediaUrl, asString(source.imageUrl)) || undefined,
    createdAt: asString(source.createdAt, asString(source.updatedAt, new Date(0).toISOString())),
    updatedAt: asString(source.updatedAt) || undefined,
  }
}

const QUESTION_TYPE_INFO: Record<string, { label: string; description: string }> = {
  multiple_choice: { label: 'Múltipla Escolha', description: 'Alias legado para pergunta fechada com varias seleções.' },
  single_choice: { label: 'Escolha Única', description: 'Somente uma opção pode ser selecionada.' },
  multiple_select: { label: 'Seleção Múltipla', description: 'Permite marcar varias opções sem ordenar.' },
  ordered_multiple: { label: 'Ranking Ordenado', description: 'Ordena itens por preferência.' },
  dropdown: { label: 'Lista Suspensa', description: 'Seleciona uma opção em menu suspenso.' },
  matrix_dropdown: { label: 'Lista Suspensa Combinada', description: 'Avalia vários itens usando a mesma lista suspensa.' },
  dropdown_matrix: { label: 'Lista Suspensa Combinada', description: 'Avalia vários itens usando a mesma lista suspensa.' },
  media: { label: 'Imagem/Video', description: 'Pergunta com apoio visual.' },
  star_rating: { label: 'Avaliação Estrelas', description: 'Escala curta de avaliação.' },
  rating_matrix: { label: 'Estrelas Combinada', description: 'Avalia vários itens com a mesma escala visual.' },
  slider: { label: 'Barra Deslizante', description: 'Escala contínua entre mínimo e máximo.' },
  slider_matrix: { label: 'Escala Deslizante Combinada', description: 'Avalia vários itens com a mesma barra deslizante.' },
  ranking: { label: 'Ranking Ordenado', description: 'Ordena itens por preferência.' },
  classification: { label: 'Classificação', description: 'Classifica cada item em um nível.' },
  best_worst: { label: 'Melhor/Pior', description: 'Comparação entre extremos de preferencia.' },
  open_text: { label: 'Texto Livre', description: 'Resposta aberta em texto.' },
  open_number: { label: 'Número', description: 'Resposta aberta numerica.' },
  multiple_textboxes: { label: 'Várias Caixas', description: 'Grupo de campos independentes.' },
  datetime: { label: 'Data/Hora', description: 'Captura data, hora ou ambos.' },
  email: { label: 'E-mail', description: 'Campo com validação de e-mail.' },
}

const QuestionBank: React.FC = () => {
  const [questions, setQuestions] = useState<Question[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [questionsPage, setQuestionsPage] = useState(1)
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [sortOrder, setSortOrder] = useState<'alphabetical_asc' | 'alphabetical_desc' | 'recent'>('alphabetical_asc')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null)

  // Função para remover HTML e extrair texto puro
  const stripHtml = (html: string) => {
    const tmp = document.createElement('div')
    tmp.innerHTML = html
    return tmp.textContent || tmp.innerText || ''
  }

  useEffect(() => {
    loadQuestions()
  }, [])

  const loadQuestions = async () => {
    try {
      setLoading(true)
      const response = await lumi.entities.questions.list({
        sort: { createdAt: -1 }
      })
      setQuestions((response.list || []).map((question, index) => normalizeQuestionRecord(question, index)))
    } catch (error) {
      console.error('Erro ao carregar perguntas:', error)
      toast.error('Erro ao carregar perguntas')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateQuestion = async (questionData: Partial<Question>) => {
    try {
      // Validar dados obrigatórios
      if (!questionData.translations || !questionData.type) {
        toast.error('Dados incompletos. Verifique todos os campos obrigatórios.')
        return
      }

      const newQuestion = await lumi.entities.questions.create({
        ...questionData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      
      setQuestions(prev => [normalizeQuestionRecord(newQuestion), ...prev])
      setShowCreateModal(false)
      toast.success('Pergunta criada com sucesso!')
    } catch (error) {
      console.error('Erro ao criar pergunta:', error)
      toast.error('Erro ao criar pergunta: ' + (error instanceof Error ? error.message : 'Erro desconhecido'))
    }
  }

  const handleUpdateQuestion = async (questionId: string, updates: Partial<Question>) => {
    try {
      const updatedQuestion = await lumi.entities.questions.update(questionId, {
        ...updates,
        updatedAt: new Date().toISOString()
      })
      
      setQuestions(prev => prev.map(q => q._id === questionId ? normalizeQuestionRecord(updatedQuestion) : q))
      setEditingQuestion(null)
      toast.success('Pergunta atualizada com sucesso!')
    } catch (error) {
      console.error('Erro ao atualizar pergunta:', error)
      toast.error('Erro ao atualizar pergunta')
    }
  }

  const handleDuplicateQuestion = async (question: Question) => {
    try {
      const duplicatedQuestion = await lumi.entities.questions.create({
        type: question.type,
        translations: {
          pt: {
            title: `${question.translations.pt.title} (Cópia)`,
            description: question.translations.pt.description,
            options: question.translations.pt.options
          },
          en: {
            title: `${question.translations.en.title} (Copy)`,
            description: question.translations.en.description,
            options: question.translations.en.options
          },
          es: {
            title: `${question.translations.es.title} (Copia)`,
            description: question.translations.es.description,
            options: question.translations.es.options
          }
        },
        isRequired: question.isRequired,
        category: question.category,
        tags: question.tags,
        settings: question.settings,
        mediaUrl: question.mediaUrl,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      
      setQuestions(prev => [normalizeQuestionRecord(duplicatedQuestion), ...prev])
      toast.success('Pergunta duplicada com sucesso!')
    } catch (error) {
      console.error('Erro ao duplicar pergunta:', error)
      toast.error('Erro ao duplicar pergunta')
    }
  }

  const handleDeleteQuestion = async (questionId: string) => {
    if (!confirm('Tem certeza que deseja excluir esta pergunta? Esta ação não pode ser desfeita.')) {
      return
    }

    try {
      await lumi.entities.questions.delete(questionId)
      setQuestions(prev => prev.filter(q => q._id !== questionId))
      toast.success('Pergunta excluída com sucesso!')
    } catch (error) {
      console.error('Erro ao excluir pergunta:', error)
      toast.error('Erro ao excluir pergunta')
    }
  }

  const filteredQuestions = questions
    .filter(question => {
      const matchesSearch = question.translations.pt.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           question.category?.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesType = typeFilter === 'all' || question.type === typeFilter
      const matchesCategory = categoryFilter === 'all' || question.category === categoryFilter
      return matchesSearch && matchesType && matchesCategory
    })
    .sort((left, right) => {
      if (sortOrder === 'recent') {
        const leftDate = new Date(left.createdAt || left.updatedAt || '').getTime() || 0
        const rightDate = new Date(right.createdAt || right.updatedAt || '').getTime() || 0
        return rightDate - leftDate
      }

      const comparison = stripHtml(left.translations.pt.title).localeCompare(
        stripHtml(right.translations.pt.title),
        'pt-BR',
        { sensitivity: 'base', numeric: true }
      )

      return sortOrder === 'alphabetical_desc' ? comparison * -1 : comparison
    })

  const questionsTotalPages = Math.max(1, Math.ceil(filteredQuestions.length / 10))
  const questionsCurrentPage = Math.min(questionsPage, questionsTotalPages)
  const questionsPageItems = filteredQuestions.slice((questionsCurrentPage - 1) * 10, questionsCurrentPage * 10)

  const getTypeLabel = (type: string) => {
    return QUESTION_TYPE_INFO[type]?.label || type
  }

  const getTypeColor = (type: string) => {
    const colors = {
      'multiple_choice': 'bg-blue-100 text-blue-800',
      'single_choice': 'bg-green-100 text-green-800',
      'multiple_select': 'bg-indigo-100 text-indigo-800',
      'ordered_multiple': 'bg-violet-100 text-violet-800',
      'dropdown': 'bg-cyan-100 text-cyan-800',
      'matrix_dropdown': 'bg-teal-100 text-teal-800',
      'dropdown_matrix': 'bg-teal-100 text-teal-800',
      'media': 'bg-pink-100 text-pink-800',
      'star_rating': 'bg-yellow-100 text-yellow-800',
      'rating_matrix': 'bg-yellow-100 text-yellow-800',
      'slider': 'bg-orange-100 text-orange-800',
      'slider_matrix': 'bg-orange-100 text-orange-800',
      'ranking': 'bg-lime-100 text-lime-800',
      'classification': 'bg-emerald-100 text-emerald-800',
      'best_worst': 'bg-amber-100 text-amber-800',
      'open_text': 'bg-purple-100 text-purple-800',
      'open_number': 'bg-sky-100 text-sky-800',
      'multiple_textboxes': 'bg-fuchsia-100 text-fuchsia-800',
      'datetime': 'bg-rose-100 text-rose-800',
      'email': 'bg-emerald-100 text-emerald-800'
    }
    return colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-800'
  }

  const categories = [...new Set(questions.map(q => q.category).filter(Boolean))]

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-900">Banco de Perguntas</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-colors"
        >
          <Plus size={20} />
          <span>Nova Pergunta</span>
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar perguntas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex items-center space-x-2">
              <Filter size={20} className="text-gray-400" />
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Todos os Tipos</option>
                {Object.entries(QUESTION_TYPE_INFO).map(([type, config]) => (
                  <option key={type} value={type}>{config.label}</option>
                ))}
              </select>
            </div>

            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todas as Categorias</option>
              {categories.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>

            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as 'alphabetical_asc' | 'alphabetical_desc' | 'recent')}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="alphabetical_asc">Ordem alfabética (A-Z)</option>
              <option value="alphabetical_desc">Ordem alfabética (Z-A)</option>
              <option value="recent">Mais recentes</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lista de perguntas */}
      <div className="space-y-3">
        {filteredQuestions.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
            <HelpCircle size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm || typeFilter !== 'all' || categoryFilter !== 'all' ? 'Nenhuma pergunta encontrada' : 'Nenhuma pergunta criada'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || typeFilter !== 'all' || categoryFilter !== 'all' 
                ? 'Tente ajustar os filtros de busca'
                : 'Comece criando sua primeira pergunta'
              }
            </p>
            {!searchTerm && typeFilter === 'all' && categoryFilter === 'all' && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Criar Primeira Pergunta
              </button>
            )}
          </div>
        ) : (
          questionsPageItems.map((question) => (
            <div key={question._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(question.type)}`}>
                    {getTypeLabel(question.type)}
                  </span>
                  {question.isRequired && (
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                      Obrigatória
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-1">
                  <Globe size={16} className="text-gray-400" />
                  <span className="text-sm text-gray-500">PT/EN/ES</span>
                </div>
              </div>

              {QUESTION_TYPE_INFO[question.type]?.description && (
                <p className="mb-2 text-xs font-medium uppercase tracking-[0.12em] text-gray-400">
                  {QUESTION_TYPE_INFO[question.type].description}
                </p>
              )}

              <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                {stripHtml(question.translations.pt.title)}
              </h3>

              {question.translations.pt.description && (
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                  {stripHtml(question.translations.pt.description)}
                </p>
              )}

              <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                {question.category && (
                  <div className="flex items-center space-x-1">
                    <Tag size={16} />
                    <span>{question.category}</span>
                  </div>
                )}
                <span>Criado em {new Date(question.createdAt).toLocaleDateString('pt-BR')}</span>
              </div>

              {question.translations.pt.options && question.translations.pt.options.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Opções:</p>
                  <div className="space-y-1">
                    {question.translations.pt.options.slice(0, 3).map((option, index) => (
                      <div key={index} className="text-sm text-gray-600 bg-gray-50 px-2 py-1 rounded">
                        {option}
                      </div>
                    ))}
                    {question.translations.pt.options.length > 3 && (
                      <div className="text-sm text-gray-500 italic">
                        +{question.translations.pt.options.length - 3} opções...
                      </div>
                    )}
                  </div>
                </div>
              )}

              {question.tags && question.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-4">
                  {question.tags.map((tag, index) => (
                    <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setEditingQuestion(question)}
                  className="flex items-center space-x-1 px-3 py-1 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  <Edit size={16} />
                  <span>Editar</span>
                </button>
                
                <button
                  onClick={() => handleDuplicateQuestion(question)}
                  className="flex items-center space-x-1 px-3 py-1 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <Copy size={16} />
                  <span>Duplicar</span>
                </button>
                
                <button
                  onClick={() => handleDeleteQuestion(question._id)}
                  className="flex items-center space-x-1 px-3 py-1 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 size={16} />
                  <span>Excluir</span>
                </button>
              </div>
            </div>
          ))
        )}
        {filteredQuestions.length > 0 && (
          <div className="mt-4 flex flex-col items-center justify-between gap-3 border-t border-gray-100 pt-4 text-sm text-gray-600 sm:flex-row">
            <span>
              Pagina {questionsCurrentPage} de {questionsTotalPages} · {filteredQuestions.length} registro(s)
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={questionsCurrentPage <= 1}
                onClick={() => setQuestionsPage(questionsCurrentPage - 1)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Anterior
              </button>
              <button
                type="button"
                disabled={questionsCurrentPage >= questionsTotalPages}
                onClick={() => setQuestionsPage(questionsCurrentPage + 1)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Proxima
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de criação/edição */}
      {(showCreateModal || editingQuestion) && (
        <QuestionFormModal
          question={editingQuestion}
          onSave={editingQuestion ? 
            (data) => handleUpdateQuestion(editingQuestion._id, data) : 
            handleCreateQuestion
          }
          onClose={() => {
            setShowCreateModal(false)
            setEditingQuestion(null)
          }}
        />
      )}
    </div>
  )
}

// Modal de formulário de pergunta
const QuestionFormModal: React.FC<{
  question?: Question | null
  onSave: (data: Partial<Question>) => void
  onClose: () => void
}> = ({ question, onSave, onClose }) => {
  const [currentLanguage, setCurrentLanguage] = useState<'pt' | 'en' | 'es'>('pt')
  const [formData, setFormData] = useState({
    type: question?.type || 'open_text',
    category: question?.category || '',
    isRequired: question?.isRequired || false,
    tags: question?.tags?.join(', ') || '',
    translations: {
      pt: {
        title: question?.translations?.pt?.title || '',
        description: question?.translations?.pt?.description || '',
        options: question?.translations?.pt?.options?.join('\n') || ''
      },
      en: {
        title: question?.translations?.en?.title || '',
        description: question?.translations?.en?.description || '',
        options: question?.translations?.en?.options?.join('\n') || ''
      },
      es: {
        title: question?.translations?.es?.title || '',
        description: question?.translations?.es?.description || '',
        options: question?.translations?.es?.options?.join('\n') || ''
      }
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validar que pelo menos o título em português está preenchido
    if (!formData.translations.pt.title.trim()) {
      toast.error('O título em português é obrigatório')
      return
    }

    const submitData: Partial<Question> = {
      type: formData.type as Question['type'],
      category: formData.category || undefined,
      isRequired: formData.isRequired,
      tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0),
      translations: {
        pt: {
          title: formData.translations.pt.title.trim(),
          description: formData.translations.pt.description.trim() || undefined,
          options: formData.translations.pt.options 
            ? formData.translations.pt.options.split('\n').map(opt => opt.trim()).filter(opt => opt.length > 0)
            : undefined
        },
        en: {
          title: formData.translations.en.title.trim() || formData.translations.pt.title.trim(),
          description: formData.translations.en.description.trim() || formData.translations.pt.description.trim() || undefined,
          options: formData.translations.en.options 
            ? formData.translations.en.options.split('\n').map(opt => opt.trim()).filter(opt => opt.length > 0)
            : (formData.translations.pt.options 
                ? formData.translations.pt.options.split('\n').map(opt => opt.trim()).filter(opt => opt.length > 0)
                : undefined)
        },
        es: {
          title: formData.translations.es.title.trim() || formData.translations.pt.title.trim(),
          description: formData.translations.es.description.trim() || formData.translations.pt.description.trim() || undefined,
          options: formData.translations.es.options 
            ? formData.translations.es.options.split('\n').map(opt => opt.trim()).filter(opt => opt.length > 0)
            : (formData.translations.pt.options 
                ? formData.translations.pt.options.split('\n').map(opt => opt.trim()).filter(opt => opt.length > 0)
                : undefined)
        }
      }
    }

    onSave(submitData)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            {question ? 'Editar Pergunta' : 'Nova Pergunta'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Tipo de Pergunta e Categoria */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Pergunta *
                </label>
                <select
                  required
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="open_text">Texto Livre</option>
                  <option value="open_number">Número</option>
                  <option value="single_choice">Escolha Única</option>
                  <option value="multiple_choice">Múltipla Escolha</option>
                  <option value="multiple_select">Seleção Múltipla</option>
                  <option value="ordered_multiple">Ranking Ordenado</option>
                  <option value="dropdown">Lista Suspensa</option>
                  <option value="dropdown_matrix">Lista Suspensa Combinada</option>
                  <option value="star_rating">Avaliação Estrelas</option>
                  <option value="rating_matrix">Estrelas Combinada</option>
                  <option value="slider">Barra Deslizante</option>
                  <option value="slider_matrix">Escala Deslizante Combinada</option>
                  <option value="ranking">Ranking Ordenado</option>
                  <option value="classification">Classificação</option>
                  <option value="best_worst">Melhor/Pior</option>
                  <option value="multiple_textboxes">Várias Caixas</option>
                  <option value="datetime">Data/Hora</option>
                  <option value="email">E-mail</option>
                  <option value="media">Imagem/Video</option>
                </select>
                {QUESTION_TYPE_INFO[formData.type]?.description && (
                  <p className="mt-2 text-xs text-gray-500">
                    {QUESTION_TYPE_INFO[formData.type].description}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Categoria
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: Satisfação, Produto, Experiência"
                />
              </div>
            </div>

            {/* Tabs de Idiomas */}
            <div>
              <div className="border-b border-gray-200 mb-4">
                <nav className="-mb-px flex space-x-8">
                  {(['pt', 'en', 'es'] as const).map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => setCurrentLanguage(lang)}
                      className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                        currentLanguage === lang
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {lang === 'pt' && 'ðŸ‡§ðŸ‡· Português'}
                      {lang === 'en' && 'ðŸ‡ºðŸ‡¸ English'}
                      {lang === 'es' && 'ðŸ‡ªðŸ‡¸ Español'}
                      {lang === 'pt' && ' *'}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Conteúdo do idioma atual */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Título da Pergunta {currentLanguage === 'pt' && '*'}
                  </label>
                  <RichTextEditor
                    value={formData.translations[currentLanguage].title}
                    onChange={(value) => setFormData(prev => ({
                      ...prev,
                      translations: {
                        ...prev.translations,
                        [currentLanguage]: {
                          ...prev.translations[currentLanguage],
                          title: value
                        }
                      }
                    }))}
                    placeholder={`Digite o título da pergunta em ${currentLanguage === 'pt' ? 'português' : currentLanguage === 'en' ? 'inglês' : 'espanhol'}`}
                    height="100px"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descrição/Instrução
                  </label>
                  <RichTextEditor
                    value={formData.translations[currentLanguage].description}
                    onChange={(value) => setFormData(prev => ({
                      ...prev,
                      translations: {
                        ...prev.translations,
                        [currentLanguage]: {
                          ...prev.translations[currentLanguage],
                          description: value
                        }
                      }
                    }))}
                    placeholder="Instruções adicionais (opcional)"
                    height="80px"
                  />
                </div>

                {['multiple_choice', 'single_choice', 'multiple_select', 'dropdown', 'dropdown_matrix', 'matrix_dropdown', 'rating_matrix', 'slider_matrix', 'ranking', 'classification', 'best_worst'].includes(formData.type) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Opções de Resposta (uma por linha)
                    </label>
                    <textarea
                      value={formData.translations[currentLanguage].options}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        translations: {
                          ...prev.translations,
                          [currentLanguage]: {
                            ...prev.translations[currentLanguage],
                            options: e.target.value
                          }
                        }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Opção 1&#10;Opção 2&#10;Opção 3"
                      rows={5}
                    />
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags (separadas por vírgula)
              </label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ex: básica, importante, feedback"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isRequired"
                checked={formData.isRequired}
                onChange={(e) => setFormData(prev => ({ ...prev, isRequired: e.target.checked }))}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="isRequired" className="ml-2 text-sm text-gray-700">
                Pergunta obrigatória
              </label>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              ðŸ’¡ <strong>Dica:</strong> Se não preencher inglês ou espanhol, o conteúdo em português será usado automaticamente.
            </div>

            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {question ? 'Atualizar' : 'Criar'} Pergunta
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default QuestionBank
